import React, { useState, useEffect } from "react";
import EmailEditorModal from "./EmailEditorModal";
import { useNavigate, useLocation } from "react-router-dom";
import { Select, message,  Tooltip, type SelectProps } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import {
  checkCampaignExists,
  fetchCampaignListsAndCompany,
  autopopulateCampaignData,
  fetchCategories,
} from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
import "./CreateCampaign.scss";

const { Option } = Select;

interface LocationState {
  campaign_id?: string;
  campaign_name?: string;
}

interface CampaignData {
  campaignName: string;
  sendTo: string[];
  excludeList: string[];
  originalCampaignId?: string;
  scheduledDateTime?: dayjs.Dayjs | null;
  emailHtml?: string;
  companyNameEngagements: string;
}

const CreateCampaign: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { campaign_id } = locationState || {};
  const [sendTo, setSendTo] = useState<string[]>([]);
  const [campaignName, setCampaignName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [excludeList, setExcludeList] = useState<string[]>([]);
  const [hasShownSitemapMessage, setHasShownSitemapMessage] = useState(false);
  const [listOptions, setListOptions] = useState<AudienceListOption[]>([]);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [isAIGeneratorOn, setIsAIGeneratorOn] = useState<boolean>(true);
  const [isAIGeneratorDisabled, setIsAIGeneratorDisabled] = useState<boolean>(
    false
  );
  const [aiDisableMessage, setAiDisableMessage] = useState<string>(
    t("campaignFlow.createCampaignNew.aiDisableMessage")
  );
  const roleId = localStorage.getItem("role_id");
  const roleIdNum = roleId ? parseInt(roleId, 10) : 0;
  const isAdmin = roleIdNum === 1;

  const [campaignData, setCampaignData] = useState<CampaignData>({
    campaignName: "",
    sendTo: [],
    excludeList: [],
    originalCampaignId: undefined,
    scheduledDateTime: null,
    emailHtml: undefined,
    companyNameEngagements: "",
  });
  const renderRecipientTag: SelectProps["tagRender"] = (
    tag: Parameters<NonNullable<SelectProps["tagRender"]>>[0]
  ) => {
    const { label, closable, onClose } = tag;
    return (
      <span className="campaign-select-tag">
        <span className="campaign-select-tag__label">{label}</span>
        {closable ? (
          <span
            className="campaign-select-tag__remove"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={onClose}
          >
            x
          </span>
        ) : null}
      </span>
    );
  };

  useEffect(() => {
    let cancelled = false;

    const fetchListOptions = async () => {
      const tenantId = localStorage.getItem("tenant_id");
      const apiKey = localStorage.getItem("api_key");
      if (!tenantId || !apiKey) {return;}

      try {
        const data = await fetchCampaignListsAndCompany(tenantId, apiKey, getApiLangFromI18n(i18n));
        if (cancelled) {return;}

        const audienceLists = registerAudienceListLabels(
          toAudienceListOptions(data)
        );
        if (audienceLists.length) {
          setListOptions(audienceLists);
          const unsubscribed = audienceLists
            .filter(list => list.name.toLowerCase().includes("unsubscribed"))
            .map(list => list.name);
          setExcludeList(unsubscribed);
        }

        if (data.company_name_engagements) {
          setCompanyName(data.company_name_engagements);
          localStorage.setItem(
            "company_name_engagements",
            data.company_name_engagements
          );
        }
      } catch (err) {
        console.error("Failed to fetch list names:", err);
      }
    };

    fetchListOptions();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  useEffect(() => {
    // Hit sitemap API immediately
    const fetchSitemap = async () => {
      try {
        console.log("â³ Hitting sitemap API immediately...");
        const res = await fetchCategories(getApiLangFromI18n(i18n));

        const websiteCats =
          res?.website_categories || res?.website_urls || res?.categories;

        if (!websiteCats || websiteCats.length === 0) {
          throw new Error("No categories found");
        }

        // âœ… API success â†’ enable AI, no message
        setIsAIGeneratorDisabled(false);
        setIsAIGeneratorOn(true);
      } catch (err) {
        const sitemapMsg = t("campaignFlow.createCampaignNew.aiDisableMessage");

        setIsAIGeneratorDisabled(true);
        setIsAIGeneratorOn(false);
        setAiDisableMessage(t("campaignFlow.createCampaignNew.aiDisableMessage"));

        // Show sitemap message only once
        if (!hasShownSitemapMessage) {
          message.open({
            key: "sitemap-info",
            type: "info",
            content: sitemapMsg,
            duration: 5,
          });
          setHasShownSitemapMessage(true);
        }
      }
    };
    fetchSitemap();
  }, []);

  const isNow = selectedDate ? selectedDate.isSame(dayjs(), "minute") : false;

  const autopopulateCampaign = async (campaignId: string): Promise<void> => {
    try {
      setLoading(true);
      const tenantId = localStorage.getItem("tenant_id");
      const apiKey = localStorage.getItem("api_key");

      if (!tenantId || !apiKey) {return;}

      const data = await autopopulateCampaignData(
        parseInt(tenantId),
        campaignId,
        apiKey
      );

      if (data.status === "success") {
        setCampaignName(data.campaign_name);
        setSendTo(data.audience.split(", ").map((item: string) => item.trim()));

        if (data.dont_end_to_audience) {
          setExcludeList(
            data.dont_end_to_audience
              .split(", ")
              .map((item: string) => item.trim())
          );
        }

        setEmailHtml(data.email_html);
      }
    } catch (error) {
      console.error("Error autopopulating campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaign_id) {
      autopopulateCampaign(campaign_id);
    }
  }, [campaign_id]);

  const handleCreateCampaign = async (): Promise<void> => {
    if (!campaignName.trim()) {
      message.error(t("campaignFlow.createCampaignNew.enterCampaignName"));
      return;
    }

    if (!companyName.trim()) {
      message.error(t("campaignFlow.createCampaignNew.companyNameRequired"));
      return;
    }

    if (!sendTo.length) {
      message.error(t("campaignFlow.createCampaignNew.selectSendTo"));
      return;
    }

    const tenantId = parseInt(localStorage.getItem("tenant_id") || "0", 10);

    try {
      await checkCampaignExists({
        tenant_id: tenantId,
        campaign_name: campaignName.trim(),
      });

      const newCampaignData: CampaignData = {
        campaignName,
        sendTo,
        excludeList,
        originalCampaignId: campaign_id,
        scheduledDateTime: isNow ? null : selectedDate,
        emailHtml: emailHtml || undefined,
        companyNameEngagements: companyName.trim(),
      };

      setCampaignData(newCampaignData);

      if (isAIGeneratorOn) {
        // âœ… Navigate if AI toggle is ON
        navigate("/account/engagement/ai-email-editor", {
          state: { campaignData: newCampaignData },
        });
      } else {
        // âœ… Show popup if AI toggle is OFF
        setShowEditor(true);
      }
    } catch (err) {
      const axiosErr = err as AxiosError<{ detail?: string }>;
      const detail =
        axiosErr?.response?.data?.detail ||
        t("campaignFlow.createCampaignNew.campaignExists");
      message.error(detail);
    }
  };

  return (
    <div className="email-campaign-container">
      <div className="email-campaign-setup">
        {/* Breadcrumb */}
        <div className="page-header">
          <button
            className="back-button back-button--text button button--primary"
            onClick={() => navigate(-1)}
          >
            {t("campaignFlow.common.back")}
          </button>
          <span className="Page-header-text">
            {t("campaignFlow.common.breadcrumb")}
          </span>
        </div>

        {/* Title */}
        <h2 className="form-title">{t("campaignFlow.createCampaignNew.title")}</h2>

        {/* Campaign Name */}
        <div className="form-group">
          <label className="form-label">{t("campaignFlow.common.campaignName")}</label>
          <StyledTextInput
            placeholder={t("campaignFlow.createCampaignNew.campaignNamePlaceholder")}
            value={campaignName}
            onChange={e => setCampaignName(e.target.value)}
            className="form-input"
          />
        </div>

        {/* Company Name */}
        <div className="form-group">
          <label className="form-label">{t("campaignFlow.common.companyName")}</label>
          <Tooltip
            title={
              !isAdmin ? t("campaignFlow.createCampaignNew.onlyAdminCanEditCompany") : ""
            }
          >
            <StyledTextInput
              value={companyName}
              disabled={!isAdmin}
              onChange={e => {
                if (isAdmin) {
                  const nextCompanyName = e.target.value;
                  setCompanyName(nextCompanyName);
                  localStorage.setItem(
                    "company_name_engagements",
                    nextCompanyName
                  );
                }
              }}
              className={`form-input ${!isAdmin ? "readonly-input" : ""}`}
            />
          </Tooltip>
        </div>

        {/* Recipients Section */}
        <div className="recipients-section">
          {/* Send To */}
          <StyledDropdown
            mode="multiple"
            placeholder={t("campaignFlow.common.sendTo")}
            value={sendTo}
            onChange={value => setSendTo(value as string[])}
            tagRender={renderRecipientTag}
            maxTagCount={1}
            maxTagPlaceholder={omitted => (
              <span className="campaign-select-tag campaign-select-tag--rest">
                +{omitted.length}
              </span>
            )}
            showSearch={false}
            options={listOptions.map(list => ({
              label: list.displayName,
              value: list.name,
            }))}
            className="filter-dropdown send-to-select"
            popupClassName="filter-dropdown-menu"
          />
          {/* Donâ€™t Send To */}
          <div className="dont-send-wrapper">
            <StyledDropdown
              mode="multiple"
            placeholder={t("campaignFlow.common.dontSendTo")}
            value={excludeList}
            onChange={value => setExcludeList(value as string[])}
            tagRender={renderRecipientTag}
            maxTagCount={1}
            maxTagPlaceholder={omitted => (
              <span className="campaign-select-tag campaign-select-tag--rest">
                +{omitted.length}
              </span>
            )}
            showSearch={false}
              options={(listOptions || [])
                .filter(list => !sendTo.includes(list.name))
                .map(list => ({
                  label: list.displayName,
                  value: list.name,
                }))}
              className="filter-dropdown dont-send-select"
              popupClassName="filter-dropdown-menu"
            />
            <span className="dont-send-optional">{t("campaignFlow.common.optional")}</span>
          </div>
        </div>

        {/* AI Powered Email Generator */}
        <div className="ai-generator">
          <div className="ai-content">
            <img
              src="/Icons/spark.svg"
              alt={t("campaignFlow.createCampaignNew.aiSparkAlt")}
              className="ai-icon"
            />
            <div className="ai-text">
              <span className="ai-label">{t("campaignFlow.common.aiGeneratorTitle")}</span>
              <span className="ai-subtext">
                {t("campaignFlow.createCampaignNew.aiGeneratorSubtext")}
              </span>
            </div>
          </div>
          <Tooltip title={isAIGeneratorDisabled ? aiDisableMessage : ""}>
            <div
              className={`switch-wrapper ${
                isAIGeneratorDisabled ? "disabled" : ""
              }`}
              onClick={() => {
                if (isAIGeneratorDisabled) {
                  // Show "Please create your campaign manually" when user clicks
                  message.info(aiDisableMessage);
                }
              }}
            >
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isAIGeneratorOn}
                  disabled={isAIGeneratorDisabled}
                  onChange={e => {
                    if (!isAIGeneratorDisabled) {
                      setIsAIGeneratorOn(e.target.checked);
                    }
                  }}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </Tooltip>
        </div>
      </div>

      <div className="footer-actions">
        <button
          className="button button--outline"
          onClick={() => navigate("/account/engagement/campaign-analytics")}
        >
          {t("campaignFlow.common.cancelUpper")}
        </button>
        <button
          className="button button--primary campaign-next"
          onClick={handleCreateCampaign}
          disabled={loading}
        >
          {t("campaignFlow.common.nextUpper")}
        </button>
      </div>

      {/* Editor Modal */}
      <EmailEditorModal
        visible={showEditor}
        onClose={() => setShowEditor(false)}
        campaignData={campaignData}
      />
    </div>
  );
};

export default CreateCampaign;

